import { CurrencyCode, getCurrencyCode } from './money';
import { N2MajorApplicationStatus } from '@n2-common/serverGeneratedTypings/n2MajorApplicationStatus';
import { getCountryApp } from '@n2-common/lib/serverSideConfigs';

/* eslint-disable camelcase */
type TypesThatGoOnDataLayer = GTMEvent | GTMStartEvent | GTMEcommerceEvent | GTMUserLoginEvent | GTMIncreaseCreditEvent | GTMInsuranceEvent;
export type WindowWithDataLayer = Window & {
    dataLayer: TypesThatGoOnDataLayer[];
    preDataLayer: TypesThatGoOnDataLayer[];
};

export type GTMStartEvent = {
    'gtm.start': number;
    event: string;
};

export type GTMEvent = {
    event: GTMEventType;
    event_params: GTMEventParamsForSectionView | GTMEventParamsForCoreBankEvent;
    user_id?: string | null | undefined;
};

export type GTMUserLoginEvent = {
    event?: GTMEventType;
    web: number;
    products: string;
    insurance?: string;
    number_of_products: number;

    has_google_pay?: string;
    has_apple_pay?: string;
    user_id?: string | null | undefined;
    country_app?: string | undefined;
};

export type GTMEcommerceEvent = {
    event?: GTMEventType;
    ecommerce: GTMEventParamsEcommerce | null;
    user_id?: string | null | undefined;
    application_id?: string | null | undefined;
    application_status?: GTMLoanStatus;
    ppi_showed?: number;
};

type GTMEventParamsEcommerce = {
    currency: CurrencyCode;
    value: number;
    items: ECommerceItem[];
};

type ECommerceItem = {
    item_id?: string; //Product SKU - both product name and SKU but one of them is mandatory
    item_name: string; // "Consumer Loan", //Anuity Loan, Flex Loan, Credit Card, Deposit, PIP,...
    item_category: ProductsForEcommerceTracking; // "Loan", //Loan, Credit Card, Deposit, Insurance, ...
    item_category2?: string;
    index: number;
    item_brand: string; // "Morrowbank",
    price: number; //,	//Selected loan amount or 0 for other products
    quantity: number;
    item_list_name?: string;
    item_list_id?: string;
};

type GTMEventBase = {
    country: CountryForTracking;
    product_type: ProductTypeForTracking;
    product_name: string;
};

type GTMEventParamsForSectionView = GTMEventBase & {
    status: GTMSectionViewEvent;
    form_section: string;
    form_section_name: string;
};

export type GTMEventParamsForCoreBankEvent = GTMEventBase & {
    status: GTMCoreBankEvents;
    product_status?: GTMLoanStatus;
    application_guid?: string;
    amount?: number;
    product_signed_status?: GTMSignedStatus;
};

export type GTMIncreaseCreditEvent = {
    event?: GTMEventType;
    product_code: string;
    user_id?: string | null | undefined;
    country: CountryForTracking;
};

export type GTMInsuranceEvent = {
    event: GTMEventType;
    product_code: string;
    user_id?: string | null | undefined;
};

declare const window: WindowWithDataLayer;
export type GTMLoanStatus = 'approved' | 'declined' | 'manual declined' | 'cancelled' | 'not set';
type GTMSignedStatus = 'signed online' | 'signed offline';

type GTMInsuranceEvents = 'keep_insurance' | 'click_cancel_insurance';

type GTMECommerceEvents =
    | 'view_item'
    | 'begin_checkout'
    | 'add_to_cart'
    | 'remove_from_cart'
    | 'view_promotion'
    | 'select_promotion'
    | 'add_shipping_info'
    | 'add_payment_info';
export type ProductsForEcommerceTracking = 'NotSet' | 'Loan' | 'Credit Card' | 'Deposit' | 'Insurance';
type GTMCustomEvents = 'form_view_event' | 'core_bank_events' | 'core_bank_agent_events' | 'user_login';

type GTMEventType = GTMCustomEvents | GTMECommerceEvents | GTMIncreaseCreditEvents | GTMInsuranceEvents;
export type GTMCoreBankEvents = 'application_start' | 'application_sent' | 'application_status' | 'application_signed' | 'application_complete';

type GTMSectionViewEvent = 'loan_form_event';
export type ProductTypeForTracking = 'CLR' | 'CLA' | 'CLF' | 'CC' | 'SA' | 'MyPage';
export type CountryForTracking = Country;
export type GTMIncreaseCreditEvents =
    | 'increase_credit'
    | 'increase_credit_eligible'
    | 'increase_credit_ineligible'
    | 'increase_credit_hasapplication';
/*
status	loan_application_start
country	FI, NO, SE
loan_type	CLR, CLA, CLF
loan_name	Refinace loan, Annuity loan, Flex loan
agent_name	omaLaina.fi, rahoutu.fi, rahalaitos.fi
application_guid	943b67f4-dc9a-4120-93dd-c8ce9a1fa99c
client_id	1209613376.1663174809
session_start_url	https%3a%2f%2fwww.komplettbank.fi%2flaina%2flainat%2fpikalaina%2f
document_location_url	https%3a%2f%2fwww.komplettbank.fi%2flaina%2flainat%2fpikalaina%2f
document_referrer	https%3a%2f%2fwww.google.com%2f
*/

function getBaseParameters(loanType: ProductTypeForTracking, country: CountryForTracking): GTMEventBase {
    return {
        country: country,
        product_type: loanType,
        product_name: ProductNameFromType(loanType) || '',
    };
}

function pushToDatalayer(e: GTMEvent | GTMEcommerceEvent | GTMUserLoginEvent | GTMIncreaseCreditEvent | GTMInsuranceEvent) {
    if (!window.dataLayer) {
        window.preDataLayer = window.preDataLayer || [];

        // console.log("Unable to find GTM data layer, pushing to internal pre-datalayer {e}", e);
        window.preDataLayer.push(e);
        return;
    }
    //console.log("Pushing event to GTM data layer, {e}", e);
    window.dataLayer.push(e);
}

export const trackCoreBankEvent = (
    productType: ProductTypeForTracking,
    country: CountryForTracking,
    status: GTMCoreBankEvents,
    loanStatus?: GTMLoanStatus,
    applicationId?: string,
    isAgentApplication?: boolean,
    amount?: number,
    gpid?: Guid
) => {
    let eventParams: GTMEventParamsForCoreBankEvent = {
        ...getBaseParameters(productType, country),
        status: status,
    };

    if (applicationId) {
        eventParams = { ...eventParams, application_guid: applicationId };
    }

    if (loanStatus) {
        eventParams = { ...eventParams, product_status: loanStatus };
    }

    if (amount) {
        eventParams = { ...eventParams, amount: amount };
    }
    if (status === 'application_signed') {
        eventParams = { ...eventParams, product_signed_status: 'signed online' };
    }

    const e: GTMEvent = {
        event: isAgentApplication ? 'core_bank_agent_events' : 'core_bank_events',
        event_params: eventParams,
        user_id: gpid,
    };
    //    console.log(e)
    pushToDatalayer(e);
};

/*  event: "user_login",
    web : 1, // 0 for app in the future
    products: "deposit, credit card", // comma separated list of products
    insurance: "yes", //does the customer has PPI
    number_of_products: 2,//The amount of total products
    user_id: {GPID} //GPID as user_id
    //BONUS
    ,has_google_pay: "yes", //Has activated Google Pay for CC
    has_apple_pay: "yes" //Has activated Apple Pay for CC
    //BONUS*/

export const trackUserLogin = (products: ProductTypeForTracking[], gpid: string) => {
    const countryApp = getCountryApp();
    const loginInWeb = countryApp === undefined ? 1 : 0;
    const trackingProducts = products.map((elm) => eCommerceProductFromGeneralProductType(elm));

    const e: GTMUserLoginEvent = {
        event: 'user_login',
        web: loginInWeb,
        products: trackingProducts.join(','),
        number_of_products: products.length,
        user_id: gpid,
        country_app: countryApp,
    };
    pushToDatalayer(e);
};

export const trackSectionView = (productType: ProductTypeForTracking, country: CountryForTracking, sectionId: string, sectionName: string) => {
    const e: GTMEvent = {
        event: 'form_view_event',
        event_params: {
            ...getBaseParameters(productType, country),
            status: 'loan_form_event',
            form_section: sectionId,
            form_section_name: sectionName,
        },
    };
    pushToDatalayer(e);
};

export const N2ApplicationStatusToECommerceStatus = (applicationStatus: N2MajorApplicationStatus): GTMLoanStatus => {
    switch (applicationStatus) {
        case N2MajorApplicationStatus.Declined:
            return 'declined';
        case N2MajorApplicationStatus.Aproved:
            return 'approved';
    }
    return 'not set';
};

export const trackEcommerceEvent = (
    productType: ProductTypeForTracking,
    country: CountryForTracking,
    ecommerceEvent: GTMECommerceEvents,
    value: number,
    gpid?: string,
    applicationId?: string,
    applicationStatus?: GTMLoanStatus,
    hasPpi?: boolean
) => {
    const item: ECommerceItem = {
        item_category: eCommerceProductFromGeneralProductType(productType),
        item_name: eCommerceProductFromGeneralProductType(productType),
        index: 0,
        quantity: 1,
        price: value,
        item_brand: 'Morrowbank',
    };
    const items: ECommerceItem[] = hasPpi
        ? [
              item,
              {
                  item_category: 'Insurance',
                  item_name: 'PPI',
                  index: 1,
                  quantity: 1,
                  price: (value * 0.25) / 100,
                  item_brand: 'BNP',
              },
          ]
        : [item];
    const e: GTMEcommerceEvent = {
        event: ecommerceEvent,
        ecommerce: {
            currency: getCurrencyCode(country),
            value: value,
            items: items,
        },
    };
    const evntWithGpid: GTMEcommerceEvent = gpid ? { ...e, user_id: gpid } : e;
    const eventToPush: GTMEcommerceEvent = applicationId ? { ...evntWithGpid, application_id: applicationId } : evntWithGpid;
    const finalEvnt: GTMEcommerceEvent = applicationStatus ? { ...eventToPush, application_status: applicationStatus } : eventToPush;
    pushToDatalayer({ ecommerce: null });
    pushToDatalayer(finalEvnt);
};

export const trackEcommerceInsuranceEvent = (
    productType: ProductTypeForTracking,
    country: CountryForTracking,
    ecommerceEvent: GTMECommerceEvents,
    value: number,
    numberOfTimesPPIShowed?: number
) => {
    const e: GTMEcommerceEvent = {
        event: ecommerceEvent,
        ecommerce: {
            currency: getCurrencyCode(country),
            value: value,
            items: [
                {
                    item_name: 'PPI', // Product name PPI for Loan or PPI for Credit Card
                    index: 0,
                    item_brand: 'BNP',
                    item_category: 'Insurance',
                    item_category2: 'PPI',
                    item_list_id: eCommercePPiListItemIdFromGeneralProductType(productType),
                    item_list_name: `PPI ${eCommerceProductFromGeneralProductType(productType)}`,
                    quantity: 1,
                    price: value,
                },
            ],
        },
    };
    const eventToPush: GTMEcommerceEvent = numberOfTimesPPIShowed ? { ...e, ppi_showed: numberOfTimesPPIShowed } : e;
    pushToDatalayer({ ecommerce: null });
    pushToDatalayer(eventToPush);
};

export const trackIncreaseCreditEvent = (eventType: GTMIncreaseCreditEvents, country: CountryForTracking, productCode: string, gpid?: string) => {
    const eventToTrack: GTMIncreaseCreditEvent = {
        event: eventType,
        country: country,
        product_code: productCode,
        user_id: gpid,
    };
    pushToDatalayer(eventToTrack);
};

export const trackInsuranceEvent = (eventType: GTMInsuranceEvents, productCode: string, gpid?: string) => {
    const eventToTrack: GTMInsuranceEvent = {
        event: eventType,
        product_code: productCode,
        user_id: gpid,
    };
    pushToDatalayer(eventToTrack);
};

function ProductNameFromType(loanType: ProductTypeForTracking) {
    switch (loanType) {
        case 'CLR':
            return 'Refinace loan';
        case 'CLA':
            return 'Annuity loan';
        case 'CLF':
            return 'Flex loan';
    }
}

function eCommerceProductFromGeneralProductType(productType: ProductTypeForTracking): ProductsForEcommerceTracking {
    switch (productType) {
        case 'CLR':
            return 'Loan';
        case 'CLA':
            return 'Loan';
        case 'CLF':
            return 'Loan';
        case 'CC':
            return 'Credit Card';
        case 'SA':
            return 'Deposit';
        default:
            return 'NotSet';
    }
}
function eCommercePPiListItemIdFromGeneralProductType(productType: ProductTypeForTracking): string {
    switch (productType) {
        case 'CLR':
        case 'CLA':
        case 'CLF':
            return 'ppi_loan';
        case 'CC':
            return 'ppi_credit_card';
        default:
            return 'ppi';
    }
}
